/**
 * DashboardShell Component
 *
 * Layout wrapper for authenticated influencer dashboard pages.
 * Provides unified header with logo, tabs, and user menu in one row.
 */

import React, { useState, useRef } from 'react';
import { useAuth } from '../../context/AuthContext.jsx';
import { useCurrentInfluencerProfile } from '../../hooks/useCurrentInfluencerProfile';
import { useNotifications } from '../../hooks/useNotifications';
import FeedbackDrawer from '../feedback/FeedbackDrawer';
import NotificationCenter from '../notifications/NotificationCenter';

interface DashboardShellProps {
  children: React.ReactNode;
  onNavigate: (page: string, id?: string | null) => void;
}

const DashboardShell: React.FC<DashboardShellProps> = ({
  children,
  onNavigate,
}) => {
  const { user, profile, signOut } = useAuth();
  const { influencerProfile } = useCurrentInfluencerProfile();
  const { notifications, unreadCount, loading, markAsRead } = useNotifications(user?.uid ?? null);
  const [notificationOpen, setNotificationOpen] = useState(false);
  const notificationAnchorRef = useRef<HTMLButtonElement>(null);

  // Get current path to determine active tab
  const currentPath = typeof window !== 'undefined' ? window.location.pathname : '';

  // Influencer navigation items - Profile-only v1
  const influencerNavItems = [
    { 
      id: 'profile-edit', 
      label: 'Profile', 
      icon: '✏️',
      path: '/profile/edit',
      navigateTo: 'profile-edit'
    },
    {
      id: 'contract-tools',
      label: 'Contract Tools',
      icon: '📄',
      path: '/profile/contract-tools',
      navigateTo: 'profile-contract-tools',
    },
    {
      id: 'feedback-feed',
      label: 'Feedback',
      icon: '💬',
      path: '/feedback',
      navigateTo: 'feedback-feed',
    },
  ];

  // Brand navigation items (for future use)
  const brandNavItems = [
    { id: 'dashboard', label: 'Dashboard', icon: '📊', path: '/dashboard', navigateTo: 'dashboard' },
    { id: 'create-campaign', label: 'Create Campaign', icon: '➕', path: '/brand/create-campaign', navigateTo: 'create-campaign' },
    { id: 'campaigns', label: 'My Campaigns', icon: '📋', path: '/brand/campaigns', navigateTo: 'my-campaigns' },
    { id: 'applications', label: 'Applications', icon: '📝', path: '/brand/applications', navigateTo: 'brand-applications' },
    { id: 'settings', label: 'Settings', icon: '⚙️', path: '/settings', navigateTo: 'settings' }
  ];

  // Get navigation items based on user role
  const navItems = profile?.userType === 'brand' ? brandNavItems : influencerNavItems;

  const handleNavClick = (item: typeof navItems[0]) => {
    onNavigate(item.navigateTo);
  };

  // Determine if a tab is active based on current path
  const isActive = (item: typeof navItems[0]) => {
    return currentPath === item.path || currentPath.startsWith(item.path + '/');
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      onNavigate('login');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  // Get user display info
  const isInfluencer = profile?.userType === 'influencer';
  const effectiveProfile = isInfluencer ? influencerProfile : profile;
  const displayName = isInfluencer
    ? (effectiveProfile?.displayName || effectiveProfile?.identity?.displayName || profile?.displayName || 'User')
    : (profile?.displayName || profile?.companyName || profile?.name || 'User');
  const avatarUrl = isInfluencer
    ? (profile?.photoURL || effectiveProfile?.photoURL || effectiveProfile?.profileImageUrl || effectiveProfile?.avatarUrl || null)
    : (profile?.logoUrl || profile?.photoUrl || profile?.profile?.media?.profilePictureUrl || profile?.profilePictureUrl || profile?.avatarUrl || profile?.photoURL || null);

  return (
    <div className="min-h-screen">
      {/* Unified header with logo, tabs, and user menu */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* LEFT: Logo + Tabs */}
            <div className="flex items-center gap-3">
              {/* InfluenceHub logo */}
              <img 
                src="/LogoSquare.png" 
                alt="InfluenceHub" 
                className="h-8 w-8 flex-shrink-0"
              />
              {/* InfluenceHub title */}
              <h1 className="text-xl font-bold text-gray-900">
                InfluenceHub
              </h1>

              {/* Navigation tabs */}
              {user && profile && (
                <nav className="flex items-center gap-1">
                  {navItems.map((item) => {
                    const active = isActive(item);
                    return (
                      <button
                        key={item.id}
                        onClick={() => handleNavClick(item)}
                        className={`
                          inline-flex items-center gap-2 px-4 py-2 text-sm font-medium transition-colors relative
                          ${active
                            ? 'text-blue-600 border-b-2 border-blue-600'
                            : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                          }
                        `}
                        aria-current={active ? 'page' : undefined}
                      >
                        <span className="text-base relative inline-block">
                          {item.icon}
                        </span>
                        <span>{item.label}</span>
                      </button>
                    );
                  })}
                </nav>
              )}
            </div>

            {/* RIGHT: User info + Settings + Logout */}
            {user && profile && (
              <div className="flex items-center space-x-4">
                {/* Notifications bell */}
                <div className="relative">
                  <button
                    ref={notificationAnchorRef}
                    type="button"
                    onClick={() => setNotificationOpen((prev) => !prev)}
                    aria-label={unreadCount > 0 ? `${unreadCount} unread notifications` : 'Notifications'}
                    aria-expanded={notificationOpen}
                    className={`
                      relative p-2 rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2
                      ${notificationOpen ? 'text-gray-700 bg-gray-50' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'}
                    `}
                  >
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                    </svg>
                    {unreadCount > 0 && (
                      <span className="absolute top-0.5 right-0.5 flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-ihTeal px-1 text-[10px] font-semibold text-white">
                        {unreadCount > 99 ? '99+' : unreadCount}
                      </span>
                    )}
                  </button>
                  {notificationOpen && (
                    <NotificationCenter
                      notifications={notifications}
                      unreadCount={unreadCount}
                      loading={loading}
                      onMarkAsRead={markAsRead}
                      onNavigate={onNavigate}
                      onClose={() => setNotificationOpen(false)}
                      anchorRef={notificationAnchorRef}
                    />
                  )}
                </div>

                {/* User info */}
                <div className="flex items-center space-x-3">
                  <div className="text-right">
                    <p className="text-sm font-medium text-gray-900">
                      {displayName}
                    </p>
                    <p className="text-xs text-gray-500 capitalize">
                      {profile.userType}
                    </p>
                  </div>

                  {/* User avatar */}
                  <div className="w-8 h-8 rounded-full bg-slate-100 border border-slate-200 overflow-hidden flex items-center justify-center relative">
                    {avatarUrl ? (
                      <img
                        src={avatarUrl}
                        alt={displayName}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          // Fallback to icon if image fails to load
                          if (e.target) {
                            e.target.style.display = 'none';
                            const fallback = e.target.parentElement?.querySelector('.avatar-fallback');
                            if (fallback) fallback.style.display = 'flex';
                          }
                        }}
                      />
                    ) : null}
                    {/* Fallback icon */}
                    <div className={`avatar-fallback absolute inset-0 flex items-center justify-center ${avatarUrl ? 'hidden' : ''}`}>
                      <svg className="h-4 w-4 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                    </div>
                  </div>
                </div>

                {/* Settings gear icon */}
                <button
                  onClick={() => onNavigate('settings')}
                  aria-label="Settings"
                  title="Settings"
                  className={`
                    p-2 rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2
                    ${currentPath === '/settings' || currentPath.startsWith('/settings/')
                      ? 'text-gray-700 bg-gray-50'
                      : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                    }
                  `}
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </button>

                {/* Logout button */}
                <button
                  onClick={handleSignOut}
                  className="text-sm text-gray-500 hover:text-gray-700 px-3 py-2 rounded-md hover:bg-gray-100 transition-colors"
                >
                  Logout
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Main content area */}
      <main className="mx-auto min-w-0 max-w-7xl p-6 lg:p-8">
        {children}
      </main>
      <FeedbackDrawer />
    </div>
  );
};

export default DashboardShell;

